// ─────────────────────────────────────────────────────────────────────────────
// utils/legacySw.ts — der Alt-Dienst-Arbeiter der VERWALTUNGSOBERFLAECHE wird
// deregistriert (T-M21.6 — REQ-OPS-057 (a) bis (h), REQ-OPS-055 (e))
//
// ── WOFUER, und zwar gemessen (REQ-OPS-057 (g)) ─────────────────────────────
// Vom 2026-06-06 bis T-M12.12 (2026-08-11) registrierte JEDER Besuch der
// Verwaltungsoberflaeche selbsttaetig einen Dienst-Arbeiter mit dem
// Geltungsbereich `/` (Vorgabe `injectRegister`, `registerType: 'autoUpdate'`).
// T-M12.12 hat die REGISTRIERUNG entfernt — nicht die BESTEHENDEN
// REGISTRIERUNGEN. Eine Deregistrierung gab es im Repo an keiner Stelle.
// `/sw.js` wird fuer die Bildschirm-Kanaele weiter ausgeliefert und haelt nach
// `globPatterns` und `navigateFallback: '/index.html'` die VOLLSTAENDIGE
// Verwaltungs-Anwendung vor; bei Bestandsnutzern bedient die Alt-Registrierung
// die Verwaltung deshalb weiter aus dem Zwischenspeicher und aktualisiert sich
// daraus fort (`skipWaiting`, `clientsClaim`).
//
// ── DIE GRENZE, UND SIE IST DIE WICHTIGSTE (REQ-OPS-057 (h), OP-165) ───────
// DASS DIESER ZUSTAND DAS GEMELDETE FELDSYMPTOM VERURSACHT HAT, IST NICHT
// BEWIESEN. Er wird entfernt, weil er ein ZUSTAND OHNE EIGENTUEMER ist, jede
// Diagnose verfaelscht und ein Fenster fuer abweichende Programmstaende
// oeffnet — nicht, weil er ueberfuehrt waere. Wer nach diesem Bau „behoben"
// meldet, behauptet mehr, als gemessen ist.
//
// ── WAS HIER AUSDRUECKLICH NICHT GESCHIEHT ─────────────────────────────────
//  • KEIN pauschales Leeren von `CacheStorage` (REQ-OPS-057 (c)). Der
//    Zwischenspeicher haengt am URSPRUNG und ist damit GETEILT: die drei
//    Bildschirm-Ansichten halten ihre Anwendungshuelle darin, und genau die ist
//    nach REQ-SC-006/007 die Rueckfall-Sicht bei Druckerausfall. Ein pauschales
//    Leeren naehme sie ihnen. Dieses Modul beruehrt `caches` mit keiner Zeile;
//    das ist gemessen und nicht behauptet (`test/altDienstArbeiter.test.ts`).
//  • KEIN selbsttaetiges Neuladen (REQ-OPS-057 (d), REQ-OPS-054 (d)).
//    BENANNTE FOLGE: die Entfernung wirkt beim NAECHSTEN Laden. Die gerade
//    geladene Seite bleibt bis dahin von der Alt-Fassung bedient — sie wurde
//    von ihr ausgeliefert, und ein Neuladen mitten in der Arbeit des Betreibers
//    waere ein groesserer Schaden als eine Sitzung auf dem alten Stand.
//  • KEINE Registrierung. Dieses Modul registriert nichts und beruehrt die drei
//    Bildschirm-Geltungsbereiche nicht (REQ-OPS-057 (b), REQ-SC-076,
//    REQ-OV-009); sie sind mit T-M12.44/T-M12.46 gerade erst hergestellt worden.
//
// ── OP-132 WIRD HIER NICHT ENTSCHIEDEN (REQ-OPS-057 (f)) ───────────────────
// Gegenstand ist der FREMDZUSTAND: eine Registrierung, die KEINE heutige
// Auslieferung anlegt. Ob die Verwaltungsoberflaeche wieder eine PWA wird, ist
// ein Entscheid des Auftraggebers (OP-132) und faellt nicht nebenbei.
// FAELLT OP-132 AUF WEG (a) — die Verwaltung wird wieder eine PWA —, IST DIESES
// MODUL NEU ZU FASSEN, und zwar an genau einer Stelle: `istAltRegistrierung`
// stellt dann von „Geltungsbereich ist genau `<Ursprung>/`" auf „was die
// heutige Auslieferung nicht SELBST registriert" um. Praktisch heisst das: die
// eigene, dann gewollte Registrierung mit Geltungsbereich `/` traegt eine
// bekannte Skriptadresse (heute `/sw.js`), und ausgenommen wird, was zu ihr
// passt. Bis dahin gilt die engere Form — sie kommt ohne Kenntnis des kuenftigen
// Entscheids aus.
// ─────────────────────────────────────────────────────────────────────────────

import { diagnose } from '@/utils/diagnose';
import { BILDSCHIRM_SW_SCOPES } from '@/utils/kitchenPwa';

/**
 * Die Sicht auf eine Registrierung, so weit dieses Modul sie braucht.
 *
 * Bewusst ein eigener, MINIMALER Typ und nicht `ServiceWorkerRegistration`:
 * eine echte Registrierung erfuellt ihn (strukturell), und eine Attrappe im
 * Test ebenso — ohne dass der Test eine halbe Browser-Schnittstelle nachbauen
 * muesste. Was das Modul nicht liest, steht hier auch nicht.
 */
export interface AltRegistrierung {
  /** Der Geltungsbereich als VOLLE Adresse, z. B. `https://host/`. */
  readonly scope: string;
  readonly active?: { readonly scriptURL?: string } | null;
  readonly waiting?: { readonly scriptURL?: string } | null;
  readonly installing?: { readonly scriptURL?: string } | null;
  unregister(): Promise<boolean>;
}

/** Die Sicht auf `navigator.serviceWorker`, so weit dieses Modul sie braucht. */
export interface AltRegistrierungsQuelle {
  getRegistrations(): Promise<readonly AltRegistrierung[]>;
}

/** Die Stellschrauben — im Betrieb alle aus der Umgebung, im Test gestellt. */
export interface AltSwOptionen {
  /** Vorgabe: `navigator.serviceWorker`. */
  quelle?: AltRegistrierungsQuelle | null;
  /** Vorgabe: `location.origin` (ohne Schraegstrich am Ende). */
  origin?: string;
  /** Vorgabe: `location.pathname` — entscheidet ueber das Tor unten. */
  pathname?: string;
}

/** Ursprung ohne Schraegstriche am Ende — `https://host/` und `https://host`. */
function normalisiereUrsprung(origin: string): string {
  return origin.replace(/\/+$/, '');
}

/**
 * Liegt dieser Pfad in einer der drei Bildschirm-Ansichten?
 *
 * ⚠ GEPRUEFT WIRD `location.pathname` UND NICHT DER ROUTER-ZUSTAND, und das ist
 * kein Abkuerzen: dieses Modul laeuft im Bootstrap, VOR der ersten Aufloesung
 * des Routers. Ein `router.currentRoute` waere zu diesem Zeitpunkt noch die
 * Startroute und truege die Antwort nicht.
 *
 * Die drei Geltungsbereiche kommen aus `utils/kitchenPwa.ts` — EINE Quelle,
 * keine zweite Liste. Eine vierte Bildschirm-Ansicht erbt das Tor dadurch
 * automatisch, sobald sie dort eingetragen ist.
 */
export function istBildschirmPfad(pathname: string): boolean {
  return BILDSCHIRM_SW_SCOPES.some(scope => pathname.startsWith(scope));
}

/**
 * Ist das die Alt-Registrierung der Verwaltungsoberflaeche (REQ-OPS-057 (a))?
 *
 * **GENAU `<Ursprung>/` und nichts sonst.** Nicht „faengt mit dem Ursprung an",
 * nicht „enthaelt", nicht „ist nicht einer der drei" — eine dieser weicheren
 * Formen naehme frueher oder spaeter eine Bildschirm-Registrierung mit, und die
 * Bildschirm-Ansichten sind auf Kiosk-Geraeten genau das, was im Stoerfall
 * gebraucht wird.
 *
 * Fremde Urspruenge treten hier gar nicht erst auf (`getRegistrations()`
 * liefert nur den eigenen); die Pruefung nennt den Ursprung trotzdem
 * ausdruecklich, damit die Regel ohne diese Kenntnis lesbar bleibt.
 */
export function istAltRegistrierung(scope: string, origin: string): boolean {
  const ursprung = normalisiereUrsprung(origin);
  if (ursprung === '' || scope === '') return false;

  // ZWEITE SPERRE, und sie ist heute rechnerisch ueberfluessig: `<Ursprung>/`
  // ist keiner der drei Bildschirm-Geltungsbereiche. Sie steht hier fuer den
  // Tag, an dem jemand die Regel oben aufweicht (etwa zu `startsWith`) — dann
  // haelt sie REQ-OPS-057 (b) weiter. Sie ersetzt die Regel nicht, sie sichert
  // sie ab.
  if (BILDSCHIRM_SW_SCOPES.some(s => scope === `${ursprung}${s}`)) return false;

  return scope === `${ursprung}/`;
}

/** Die Skriptadresse der Registrierung — fuer die Protokollzeile. */
function skriptAdresse(registrierung: AltRegistrierung): string | null {
  return (
    registrierung.active?.scriptURL
    ?? registrierung.waiting?.scriptURL
    ?? registrierung.installing?.scriptURL
    ?? null
  );
}

/** `navigator.serviceWorker`, sofern es ihn in dieser Umgebung gibt. */
function umgebungsQuelle(): AltRegistrierungsQuelle | null {
  if (typeof navigator === 'undefined') return null;
  if (!('serviceWorker' in navigator)) return null;
  // KEINE Typumgehung: dass die echte Schnittstelle diese Sicht erfuellt, prueft
  // damit der Uebersetzer und nicht der Kommentar. Die Abfrage auf die Methode
  // bleibt trotzdem — in aelteren und in unsicheren Kontexten gibt es sie nicht,
  // und der Uebersetzer weiss davon nichts.
  const container = navigator.serviceWorker;
  if (typeof container?.getRegistrations !== 'function') return null;
  return container;
}

/**
 * Entfernt jede Registrierung, deren Geltungsbereich genau `<Ursprung>/` lautet
 * (REQ-OPS-057 (a)). Rueckgabe: die entfernten Geltungsbereiche.
 *
 * **SIE WIRFT NIE.** Sie laeuft im Bootstrap der Anwendung, und keiner ihrer
 * erwartbaren Fehlschlaege darf den Start aufhalten: in der Testumgebung und in
 * aelteren oder unsicheren Kontexten gibt es `navigator.serviceWorker` nicht,
 * `getRegistrations()` darf ablehnen, und `unregister()` darf fehlschlagen. Das
 * Aufraeumen eines Fremdzustands ist Kuer; der Start ist Pflicht.
 *
 * **DAS TOR (REQ-OPS-057 (a)): in den drei Bildschirm-Ansichten laeuft sie
 * nicht.** Es steht ZWEIMAL — sichtbar am Aufruf in `main.ts` und noch einmal
 * hier. Das ist Absicht: am Aufruf ist es lesbar, hier ist es nicht zu umgehen.
 */
export async function entferneAltenDienstArbeiter(optionen: AltSwOptionen = {}): Promise<string[]> {
  const entfernt: string[] = [];
  try {
    const pathname = optionen.pathname
      ?? (typeof location === 'undefined' ? '' : location.pathname);
    if (istBildschirmPfad(pathname)) return entfernt;

    const origin = optionen.origin
      ?? (typeof location === 'undefined' ? '' : location.origin);
    if (normalisiereUrsprung(origin) === '') return entfernt;

    const quelle = optionen.quelle ?? umgebungsQuelle();
    if (!quelle) return entfernt;

    const registrierungen = await quelle.getRegistrations();
    for (const registrierung of registrierungen ?? []) {
      if (!istAltRegistrierung(registrierung?.scope ?? '', origin)) continue;
      const scope     = registrierung.scope;
      const scriptUrl = skriptAdresse(registrierung);
      try {
        await registrierung.unregister();
      } catch {
        // Fehlgeschlagene Entfernung: keine Zeile, kein Abbruch. Eine Zeile
        // steht NUR, wenn wirklich entfernt wurde (REQ-OPS-057 (e)).
        continue;
      }
      entfernt.push(scope);
      // Die eine Zeile: Geltungsbereich und Skriptadresse (REQ-OPS-057 (e)).
      // Sie steht je ENTFERNUNG und nicht je Start — ein Uebergang, kein
      // Zustand (REQ-OPS-055 (e)). Im Regelfall gibt es sie genau einmal je
      // Browser, danach nie wieder.
      diagnose('info', 'serviceWorker.legacyUnregistered', { scope, scriptUrl });
    }
  } catch {
    // Siehe oben: der Start ist Pflicht.
  }
  return entfernt;
}
